import { lazy, Suspense, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { formatValue } from '../../lib/format';
import { Icon } from '../../ui/Icon';
import { CellChange, isNumericCol, renderCell, type Col } from '../../ui/grid/cells';
import type { RegistryEntry, TableModel, WidgetComponentProps } from '../types';
import { cardColumns } from './card-columns';

export { renderCell, type Col } from '../../ui/grid/cells';

// The Data Grid (MUI) is loaded on demand, so pages without tables don't pay for it.
const DataGridTable = lazy(() => import('../../ui/grid/DataGridTable'));

/** Analytics tables: MUI Data Grid with sorting, filtering, pagination and resizable columns. */
export function GridTable(props: { columns: Col[]; rows: Record<string, unknown>[]; totals?: Record<string, unknown> | null; pageSize?: number; caption?: string }) {
  return (
    <Suspense fallback={<div className="skeleton" style={{ flex: 1, minHeight: 120 }} aria-label="Loading table" />}>
      <DataGridTable {...props} />
    </Suspense>
  );
}

/** Plain table for admin lists with row actions (menus inside grid cells would be clipped). */
export function Table({
  columns,
  rows,
  totals,
  pageSize = 0,
  rowActions,
  caption,
  emptyText = 'No rows',
}: {
  columns: Col[];
  rows: Record<string, unknown>[];
  totals?: Record<string, unknown> | null;
  pageSize?: number;
  rowActions?: (row: Record<string, unknown>) => ReactNode;
  caption?: string;
  /** Shown in place of rows when there are none. */
  emptyText?: string;
}) {
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const [page, setPage] = useState(0);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    return [...rows].sort((a, b) => {
      const x = a[sort.key];
      const y = b[sort.key];
      if (x === y) return 0;
      if (x === null || x === undefined) return 1;
      if (y === null || y === undefined) return -1;
      return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))) * sort.dir;
    });
  }, [rows, sort]);
  const pages = pageSize ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const view = pageSize ? sorted.slice(page * pageSize, page * pageSize + pageSize) : sorted;
  const isNum = isNumericCol;
  return (
    <>
      <div className={`table-wrap${view.length === 0 ? ' empty' : ''}`}>
        <table className="data">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={isNum(c) ? 'num' : ''} aria-sort={sort?.key === c.key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
                  <button onClick={() => setSort((s) => (s?.key === c.key ? { key: c.key, dir: s.dir === 1 ? -1 : 1 } : { key: c.key, dir: isNum(c) ? -1 : 1 }))}>
                    {c.label}
                    {sort?.key === c.key && <Icon name={sort.dir === 1 ? 'chevronUp' : 'chevronDown'} size={12} />}
                  </button>
                </th>
              ))}
              {rowActions && <th aria-label="Actions" className="actions-col" />}
            </tr>
          </thead>
          <tbody>
            {view.map((r, i) => (
              <tr key={String(r.id ?? r.key ?? i)}>
                {columns.map((c, ci) => (
                  <td
                    key={c.key}
                    className={`${isNum(c) ? 'num' : ''}${c.emphasis ? ' emph' : ''}`}
                    title={ci === 0 && typeof r[c.key] === 'string' ? (r[c.key] as string) : undefined}
                  >
                    {renderCell(c, r[c.key], r)}
                    {r.changes ? <CellChange v={(r.changes as Record<string, unknown>)[c.metric ?? c.key]} direction={c.direction} /> : null}
                  </td>
                ))}
                {rowActions && <td className="actions-col">{rowActions(r)}</td>}
              </tr>
            ))}
          </tbody>
          {totals && (
            <tfoot>
              <tr>
                {columns.map((c, i) => (
                  <td key={c.key} className={isNum(c) ? 'num' : ''}>
                    {i === 0 ? 'Total' : c.format === 'status' ? '' : formatValue(totals[c.key], c.format, { currency: c.currency })}
                  </td>
                ))}
                {rowActions && <td className="actions-col" />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {/* Outside the sideways-scrolling table so the message wraps at the card's width. */}
      {view.length === 0 && <p className="table-empty muted">{emptyText}</p>}
      {pageSize > 0 && pages > 1 && (
        <div className="pager">
          <span>
            {page * pageSize + 1}–{Math.min(sorted.length, (page + 1) * pageSize)} of {sorted.length}
          </span>
          <button className="btn sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <button className="btn sm" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}
    </>
  );
}

/** Resolve display columns: schema props.columns win; metric columns borrow format/currency from data. */
export function resolveColumns(data: any, props: any): Col[] {
  const metricCols: any[] = data?.columns ?? [];
  const byId = new Map(metricCols.map((c) => [c.id ?? c.key, c]));
  if (props.columns?.length) {
    return props.columns.map((c: any) => {
      const mc = c.metric ? byId.get(c.metric) : undefined;
      const format = c.format ?? mc?.format;
      return { ...c, format, direction: c.direction ?? mc?.direction, currency: format === 'currency' && !c.currencyKey ? data?.currency ?? null : null };
    });
  }
  if (metricCols.length && metricCols[0].id) {
    return [
      { key: 'label', label: data?.byLabel ?? 'Name' },
      ...metricCols.map((c) => ({ key: c.id, label: c.label, format: c.format, direction: c.direction, currency: c.monetary ? data?.currency : null })),
    ];
  }
  if (metricCols.length) return metricCols.map((c) => ({ ...c, currency: c.format === 'currency' ? data?.currency : null }));
  const first = data?.rows?.[0];
  return first ? Object.keys(first).filter((k) => typeof first[k] !== 'object').map((k) => ({ key: k, label: k })) : [];
}

/** Which columns head each card (by key); the rest are listed under the main value. */
interface CardSpec {
  title?: string;
  subtitle?: string;
  value?: string;
}

/**
 * Rows as cards (props.view = "cards"): a title and subtitle, one main value, the changes as
 * coloured arrows, then every other column as label → value. Totals lead as a full-width "all"
 * summary. The layout follows the widget's own width (container queries), not the screen's.
 */
export function RowCards({ columns, rows, totals, spec, caption }: { columns: Col[]; rows: Record<string, unknown>[]; totals?: Record<string, unknown> | null; spec?: CardSpec; caption?: string }) {
  const titleKey = spec?.title ?? columns[0]?.key;
  const subKey = spec?.subtitle;
  const valueCol = columns.find((c) => c.key === spec?.value) ?? columns.find((c) => c.key !== titleKey && c.key !== subKey && isNumericCol(c));
  const rest = columns.filter((c) => c.key !== titleKey && c.key !== subKey && c.key !== valueCol?.key);
  const changes = rest.filter((c) => c.format === 'signed_percent');
  const figures = rest.filter((c) => c.format !== 'signed_percent');
  const body = (r: Record<string, unknown>, total: boolean) => {
    const shownChanges = total ? changes.filter((c) => c.key in r) : changes;
    return (
      <>
        <div className="row-card-main">
          <div className="row-card-head">
            <span className="row-card-title">{total ? 'All stores' : String(r[titleKey!] ?? '—')}</span>
            {subKey && !total && r[subKey] ? <span className="row-card-sub">{String(r[subKey])}</span> : null}
          </div>
          {valueCol && (
            <div className="row-card-value" aria-label={valueCol.label}>
              <span className="row-card-label">{valueCol.label}</span>
              <span className="row-card-big">{renderCell(valueCol, r[valueCol.key], r)}</span>
            </div>
          )}
        </div>
        {shownChanges.length > 0 && (
          <ul className="row-card-changes" aria-label="Changes">
            {shownChanges.map((c) => (
              <li key={c.key}>
                <span className="row-card-label">{c.label}</span>
                {typeof r[c.key] === 'number' ? <CellChange v={r[c.key]} direction={c.direction} /> : <span className="muted">{r[c.key] == null ? '—' : String(r[c.key])}</span>}
              </li>
            ))}
          </ul>
        )}
        <dl className="row-card-figures">
          {figures.filter((c) => !total || r[c.key] != null).map((c) => (
            <div key={c.key}>
              <dt>{c.label}</dt>
              <dd>{renderCell(c, r[c.key], r)}</dd>
            </div>
          ))}
        </dl>
      </>
    );
  };
  // Columns follow the widget's own width and the number of cards (even rows), measured before paint.
  const wrap = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const fit = () => setCols(cardColumns(el.clientWidth, rows.length));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [rows.length]);
  return (
    <div ref={wrap} className="row-cards-wrap" style={cols ? ({ '--cols': cols } as CSSProperties) : undefined}>
      {totals && (
        <section className="row-card total" aria-label="All stores">
          {body(totals, true)}
        </section>
      )}
      <ul className="row-cards" aria-label={caption}>
        {rows.map((r) => (
          <li key={String(r.key ?? r.id ?? r[titleKey!])} className="row-card">
            {body(r, false)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DataTable({ data, props, widget }: WidgetComponentProps) {
  const cols = resolveColumns(data, props);
  if (props.view === 'cards') return <RowCards columns={cols} rows={data?.rows ?? []} totals={props.totals ? data?.totals : null} spec={props.card} caption={widget.title} />;
  return <GridTable columns={cols} rows={data?.rows ?? []} totals={props.totals ? data?.totals : null} pageSize={props.pageSize ?? 0} caption={widget.title} />;
}

export const dataTable: RegistryEntry = {
  component: DataTable,
  scroll: true,
  isEmpty: (d) => !d?.rows?.length,
};

export function TableView({ model, title }: { model: TableModel; title: string }) {
  return <GridTable columns={model.columns} rows={model.rows} caption={`${title} — table view`} />;
}
